import React from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';

/**
 * Executive Gold Theme - Premium Corporate Look
 * Gold accents, elegant typography, executive styling
 */
const ExecutiveGoldTheme = ({ document, settings = {} }) => {
  const primaryColor = settings.theme?.primary_color || '#6B7280';
  const accentColor = settings.theme?.secondary_color || '#9CA3AF';
  const fontFamily = settings.typography?.font_family || 'Georgia, serif';
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    if (num === 0) return 'Zero Rupees Only';
    
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor((num % 1000) / 100);
    const remainder = Math.floor(num % 100);
    
    let words = '';
    if (crore > 0) words += ones[crore] + ' Crore ';
    if (lakh > 0) words += (lakh < 20 && lakh > 9 ? teens[lakh - 10] : (tens[Math.floor(lakh / 10)] + ' ' + ones[lakh % 10])) + ' Lakh ';
    if (thousand > 0) words += (thousand < 20 && thousand > 9 ? teens[thousand - 10] : (tens[Math.floor(thousand / 10)] + ' ' + ones[thousand % 10])) + ' Thousand ';
    if (hundred > 0) words += ones[hundred] + ' Hundred ';
    if (remainder > 0) {
      if (remainder < 10) words += ones[remainder];
      else if (remainder < 20) words += teens[remainder - 10];
      else words += tens[Math.floor(remainder / 10)] + ' ' + ones[remainder % 10];
    }
    
    return words.trim() + ' Rupees Only';
  };

  return (
    <div className="print-template executive-gold" style={{ fontFamily, padding: '40px', maxWidth: '210mm', margin: '0 auto', background: '#fff' }}>
      {/* Elegant Border */}
      <div style={{ border: `3px double ${primaryColor}`, padding: '30px', position: 'relative' }}>
        {/* Corner Accents */}
        <div style={{ position: 'absolute', top: '-2px', left: '-2px', width: '40px', height: '40px', borderTop: `3px solid ${primaryColor}`, borderLeft: `3px solid ${primaryColor}` }}></div>
        <div style={{ position: 'absolute', top: '-2px', right: '-2px', width: '40px', height: '40px', borderTop: `3px solid ${primaryColor}`, borderRight: `3px solid ${primaryColor}` }}></div>
        <div style={{ position: 'absolute', bottom: '-2px', left: '-2px', width: '40px', height: '40px', borderBottom: `3px solid ${primaryColor}`, borderLeft: `3px solid ${primaryColor}` }}></div>
        <div style={{ position: 'absolute', bottom: '-2px', right: '-2px', width: '40px', height: '40px', borderBottom: `3px solid ${primaryColor}`, borderRight: `3px solid ${primaryColor}` }}></div>

        {/* Header with Accent Bar */}
        <div style={{ borderBottom: `4px solid ${primaryColor}`, paddingBottom: '20px', marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              {settings.branding?.logo_url && (
                <img src={settings.branding.logo_url} alt="Logo" style={{ maxHeight: '80px', marginBottom: '15px' }} />
              )}
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#B8860B', margin: '0 0 5px 0', letterSpacing: '1px' }}>
                {settings.branding?.company_name || 'COMPANY NAME'}
              </h1>
              <p style={{ fontSize: '11px', color: '#666', lineHeight: '1.6', maxWidth: '300px' }}>
                {settings.branding?.address || '123 Business Street, City, State - 123456'}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`, color: '#fff', padding: '15px 25px', borderRadius: '8px', marginBottom: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', letterSpacing: '2px', marginBottom: '5px' }}>QUOTATION</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{document.quotation_no || 'QT-001'}</div>
              </div>
              <div style={{ fontSize: '11px', color: '#666', textAlign: 'right' }}>
                <div><strong>Date:</strong> {new Date(document.doc_date || Date.now()).toLocaleDateString('en-IN')}</div>
                {document.meta?.valid_days && (
                  <div><strong>Valid Till:</strong> {new Date(Date.now() + document.meta.valid_days * 86400000).toLocaleDateString('en-IN')}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bill To Section - Elegant Box */}
        <div style={{ background: '#FFF9E6', border: '1px solid #B8860B', borderRadius: '8px', padding: '20px', marginBottom: '30px' }}>
          <div style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '2px', color: '#B8860B', marginBottom: '10px' }}>BILL TO</div>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333', marginBottom: '8px' }}>{document.party?.name || 'Customer Name'}</div>
          {document.party?.phone && <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Phone: {document.party.phone}</div>}
          {document.party?.billing_address && (
            <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.6', marginTop: '8px' }}>
              {document.party.billing_address.line1}<br />
              {document.party.billing_address.city}, {document.party.billing_address.state} - {document.party.billing_address.pincode}
            </div>
          )}
          {settings.toggles?.show_gstin && document.party?.gstin && (
            <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}><strong>GSTIN:</strong> {document.party.gstin}</div>
          )}
        </div>

        {/* Items Table - Executive Style */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
          <thead>
            <tr style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`, color: '#fff' }}>
              <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' }}>#</th>
              <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' }}>DESCRIPTION</th>
              {settings.toggles?.show_hsn && <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' }}>HSN</th>}
              <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' }}>QTY</th>
              <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' }}>RATE</th>
              <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {(document.items || []).map((item, index) => (
              <tr key={index} style={{ borderBottom: '1px solid #E8E8E8' }}>
                <td style={{ padding: '12px 10px', fontSize: '11px', color: '#666' }}>{index + 1}</td>
                <td style={{ padding: '12px 10px', fontSize: '12px', color: '#333', fontWeight: '500' }}>
                  {item.name}
                  {item.description && <div style={{ fontSize: '10px', color: '#999', marginTop: '3px' }}>{item.description}</div>}
                </td>
                {settings.toggles?.show_hsn && <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: '11px', color: '#666' }}>{item.hsn || '-'}</td>}
                <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', color: '#666' }}>{item.quantity} {item.unit || 'PCS'}</td>
                <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '11px', color: '#666' }}>{formatCurrency(item.unit_price)}</td>
                <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '12px', color: '#333', fontWeight: '600' }}>{formatCurrency(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals Section - Premium Gold Box */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
          <div style={{ width: '400px', border: `2px solid ${primaryColor}`, borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ background: '#F9FAFB', padding: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px' }}>
                <span style={{ color: '#666' }}>Subtotal:</span>
                <span style={{ fontWeight: '600', color: '#333' }}>{formatCurrency(document.totals?.subtotal || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px' }}>
                <span style={{ color: '#666' }}>Discount:</span>
                <span style={{ fontWeight: '600', color: '#DC2626' }}>- {formatCurrency(document.totals?.discount_total || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '12px', paddingTop: '8px', borderTop: '1px solid #DDD' }}>
                <span style={{ color: '#666', fontWeight: '600' }}>Taxable Amount:</span>
                <span style={{ fontWeight: '600', color: '#333' }}>{formatCurrency(document.totals?.taxable_amount || 0)}</span>
              </div>
              {settings.toggles?.show_tax_breakup && document.tax_breakup?.map((tax, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '11px', paddingLeft: '10px' }}>
                  <span style={{ color: '#666' }}>CGST @ {tax.rate / 2}%:</span>
                  <span style={{ fontWeight: '500', color: '#333' }}>{formatCurrency(tax.amount / 2)}</span>
                </div>
              ))}
              {settings.toggles?.show_tax_breakup && document.tax_breakup?.map((tax, idx) => (
                <div key={`sgst-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '11px', paddingLeft: '10px' }}>
                  <span style={{ color: '#666' }}>SGST @ {tax.rate / 2}%:</span>
                  <span style={{ fontWeight: '500', color: '#333' }}>{formatCurrency(tax.amount / 2)}</span>
                </div>
              ))}
              {document.totals?.round_off !== 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '11px' }}>
                  <span style={{ color: '#666' }}>Round Off:</span>
                  <span style={{ fontWeight: '500', color: '#333' }}>{document.totals.round_off > 0 ? '+' : ''} {formatCurrency(Math.abs(document.totals.round_off))}</span>
                </div>
              )}
            </div>
            <div style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`, padding: '15px', color: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', letterSpacing: '1px' }}>TOTAL AMOUNT</span>
                <span style={{ fontSize: '22px', fontWeight: 'bold' }}>{formatCurrency(document.totals?.grand_total || 0)}</span>
              </div>
              {document.payment?.received_amount && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', opacity: 0.95, marginBottom: '4px' }}>
                    <span>Received:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(document.payment.received_amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', opacity: 0.95 }}>
                    <span>Balance:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(document.totals.grand_total - document.payment.received_amount)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Amount in Words */}
        <div style={{ background: '#F9FAFB', border: `1px solid ${primaryColor}`, borderRadius: '6px', padding: '12px 15px', marginBottom: '30px' }}>
          <div style={{ fontSize: '10px', fontWeight: '600', color: primaryColor, marginBottom: '4px', letterSpacing: '1px' }}>AMOUNT IN WORDS</div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#333', textTransform: 'uppercase' }}>
            {numberToWords(document.totals?.grand_total || 0)}
          </div>
        </div>

        {/* Terms & Bank Details */}
        <div style={{ display: 'flex', gap: '20px', marginTop: '40px' }}>
          {document.terms?.notes_public && (
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#B8860B', marginBottom: '8px', letterSpacing: '1px' }}>TERMS & CONDITIONS</div>
              <div style={{ fontSize: '10px', color: '#666', lineHeight: '1.8' }}>
                {document.terms.notes_public}
              </div>
            </div>
          )}
          {settings.toggles?.show_bank_details && settings.bank_details && (
            <div style={{ width: '300px', background: '#F9FAFB', border: `1px solid ${primaryColor}`, borderRadius: '6px', padding: '15px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: primaryColor, marginBottom: '8px', letterSpacing: '1px' }}>BANK DETAILS</div>
              <div style={{ fontSize: '10px', color: '#666', lineHeight: '1.8' }}>
                <div><strong>Bank:</strong> {settings.bank_details.bank_name}</div>
                <div><strong>A/C:</strong> {settings.bank_details.account_number}</div>
                <div><strong>IFSC:</strong> {settings.bank_details.ifsc}</div>
              </div>
            </div>
          )}
        </div>

        {/* Signature Section */}
        {settings.toggles?.show_signature && (
          <div style={{ marginTop: '50px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              {settings.qr_code_data && (
                <QRCode value={settings.qr_code_data} size={80} />
              )}
            </div>
            <div style={{ textAlign: 'center', borderTop: '2px solid #B8860B', paddingTop: '10px', minWidth: '200px' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#B8860B' }}>AUTHORIZED SIGNATURE</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutiveGoldTheme;